import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import posthog from 'posthog-js'
import { captureSurveyStarted } from '@/lib/analytics'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { SurveyEntrySkeleton } from '@/shared/components/skeletons'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/query-keys'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { runDetached } from '@/shared/lib/runDetachedPromise'
import { useStartProviderResponse } from '../api/instrument-info-mutations'
import {
  useProviderInstrumentInfo,
  useProviderInstrumentStatus,
} from '../api/instrument-info-queries'
import { InstrumentInfoReadyEntry } from '../components/InstrumentInfoReadyEntry'
import { SurveyCompletionView } from '../components/survey-execution/SurveyCompletionView'
import { InstrumentPausedDialog } from '../components/InstrumentPausedDialog'
import { SurveyUnavailableView } from '../components/survey-execution/SurveyUnavailableView'
import { SurveyEntryScreen } from '../components/SurveyEntryScreen'
import {
  deriveSurveyAvailabilityBadge,
  showContinueSurveyCta,
  showStartSurveyCta,
} from '../lib/instrument-info-state'
import { wrapInstrumentInfoGuestTheme } from '../lib/instrument-info-guest-theme'
import {
  isInstrumentInfoLoadFailedState,
  isInstrumentInfoNotEligibleError,
  isInstrumentStatusPausedError,
  resolveInstrumentInfoPagePhase,
  resolveInstrumentInfoPageViewState,
  shouldShowInstrumentInfoUnavailableScreen,
} from '../lib/instrument-info-page-phase'
import { useSurveyEntryCompletionChrome } from '../lib/SurveyEntryChromeContext'
import { useSpProfile } from '@/features/rewards/api'
import {
  EKYC_UNDER_REVIEW_BLOCK_MESSAGE,
  isEkycUnderReview,
} from '@/features/ekyc/lib/ekycUi'
import { useSessionStore } from '@/stores/useSessionStore'
import {
  isGuestSurveyClaimJourney,
  isPendingSurveyUser,
  persistSurveyFirstOnboardingHandoff,
} from '../lib/pending-survey-session'
import {
  isNonSignupSurveyAutostart,
  NON_SIGNUP_SURVEY_AUTOSTART_PARAM,
} from '../lib/instrument-link-routing'
import { resolveSurveyCompletionVariant } from '../lib/survey-completion-variant'
import { useToast } from '@/shared/toast/ToastProvider'
import {
  INSTRUMENT_NOT_ELIGIBLE,
} from '../lib/instrument-info-messages'
import { returnHomeOrLoginPath } from '../lib/return-home-or-login'
import { notifySurveySessionTimedOut } from '../lib/handle-survey-session-blocked'
import { mapStartSurveyError } from '../lib/map-start-survey-error'
import type {
  InstrumentInfoEntryViewState,
  SurveyExecutionLocationState,
} from '../lib/types'
import {
  surveyEntryCardBodySx,
  surveyEntryLoadingSx,
  surveyEntryStatusAlertSx,
} from '../styles/surveyEntryStyles'

function InstrumentInfoLoadingScreen() {
  return (
    <SurveyEntryScreen>
      <Box sx={surveyEntryLoadingSx}>
        <SurveyEntrySkeleton />
      </Box>
    </SurveyEntryScreen>
  )
}

const AUTOSTART_STORAGE_PREFIX = 'bf-nonsignup-survey-autostart:'

export function InstrumentInfoPage() {
  const { instrumentId } = useParams<{ instrumentId: string }>()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const autoStartRequested = isNonSignupSurveyAutostart(searchParams.toString())
  const autoStartHandledKey = useRef<string | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const userId = useSessionStore((s) => s.user?.id)
  const userGuest = useSessionStore((s) => isGuestSurveyClaimJourney(s.user))
  const surveyFirstJourney = useSessionStore((s) => isPendingSurveyUser(s.user))
  const wrapGuestTheme = (node: ReactElement) =>
    wrapInstrumentInfoGuestTheme(userGuest, node)

  const infoQuery = useProviderInstrumentInfo(instrumentId)
  const statusQuery = useProviderInstrumentStatus(instrumentId)
  const { data: profile } = useSpProfile()
  const underReview = isEkycUnderReview(profile?.ekycStatus)
  const startMutation = useStartProviderResponse()
  const { showToast } = useToast()

  const [startErrorMessage, setStartErrorMessage] = useState<string | null>(
    null
  )
  const [viewStateOverride, setViewStateOverride] =
    useState<InstrumentInfoEntryViewState | null>(null)

  const info = infoQuery.data
  const status = statusQuery.data

  useDocumentTitle(info?.name ?? 'Survey')

  useEffect(() => {
    if (!info || !instrumentId) return
    posthog.capture('survey_viewed', {
      instrument_id: instrumentId,
      survey_name: info.name,
      reward_enabled: info.reward.enabled,
    })
  }, [info, instrumentId])

  const infoNotEligible = isInstrumentInfoNotEligibleError(
    infoQuery.isError,
    infoQuery.error,
  )
  const statusNotEligible = isInstrumentInfoNotEligibleError(
    statusQuery.isError,
    statusQuery.error,
  )

  const viewState = resolveInstrumentInfoPageViewState({
    viewStateOverride,
    infoNotEligible,
    statusNotEligible,
    info,
    status,
  })

  const showNotEligible = viewState === 'notEligible'
  const showUnavailableScreen = shouldShowInstrumentInfoUnavailableScreen({
    showNotEligible,
    viewState,
    statusQueryError: statusQuery.isError,
    statusQueryPausedError: isInstrumentStatusPausedError(
      statusQuery.isError,
      statusQuery.error,
    ),
    hasInfo: Boolean(info),
  })

  useSurveyEntryCompletionChrome(viewState === 'alreadyCompleted' || showUnavailableScreen)

  const goHomeOrLogin = useCallback(() => {
    navigate(returnHomeOrLoginPath(useSessionStore.getState().isAuthenticated), {
      replace: true,
    })
  }, [navigate])

  const statusReady =
    Boolean(status) && !statusQuery.isLoading && !statusQuery.isError
  const canStart = showStartSurveyCta({ viewState, status })
  const canContinue = showContinueSurveyCta({ status, viewState })
  const activeResponseId =
    status?.activeResponseId ?? status?.resumableResponseId ?? null

  const navigateToSurvey = useCallback(
    (responseId: string, definition?: unknown) => {
      if (!instrumentId) return
      const state: SurveyExecutionLocationState = {
        responseId,
        instrumentId,
        definition,
      }
      navigate(`/instrument/${instrumentId}/survey`, { state })
    },
    [instrumentId, navigate]
  )

  const runStart = useCallback(async () => {
    if (!instrumentId) return
    setStartErrorMessage(null)
    if (underReview) {
      setStartErrorMessage(EKYC_UNDER_REVIEW_BLOCK_MESSAGE)
      return
    }
    try {
      const result = await startMutation.mutateAsync(instrumentId)
      captureSurveyStarted({
        surveyId: instrumentId,
        surveyName: info?.name,
        campaignId: result.response.campaignId ?? info?.campaignId,
        responseId: result.response.responseId,
        rewardAmount: info?.reward.amount,
      })
      navigateToSurvey(result.response.responseId, result.definition)
    } catch (error) {
      const mapped = mapStartSurveyError(error)
      if (mapped.sessionTimeout) {
        notifySurveySessionTimedOut(showToast, navigate, mapped.message)
        return
      }
      setStartErrorMessage(mapped.message)
      if (mapped.viewStateOverride) {
        setViewStateOverride(mapped.viewStateOverride)
        runDetached(
          queryClient.invalidateQueries({
            queryKey: queryKeys.spInstrument.status(instrumentId),
          }),
        )
      }
    }
  }, [
    info,
    instrumentId,
    navigate,
    navigateToSurvey,
    queryClient,
    showToast,
    startMutation,
    underReview,
  ])

  const handleContinueClick = useCallback(() => {
    if (!activeResponseId) return
    posthog.capture('survey_continued', {
      instrument_id: instrumentId,
      survey_name: info?.name,
      response_id: activeResponseId,
    })
    navigateToSurvey(activeResponseId)
  }, [activeResponseId, instrumentId, info, navigateToSurvey])

  const handleStartSurveyClick = useCallback(() => {
    runDetached(runStart())
  }, [runStart])

  const infoLoadFailed = isInstrumentInfoLoadFailedState({
    info,
    infoLoading: infoQuery.isLoading,
    infoError: infoQuery.isError,
  })

  useEffect(() => {
    if (!autoStartRequested || !instrumentId || !info) return
    if (infoQuery.isLoading || !infoQuery.isFetched) return
    if (statusQuery.isLoading || !statusQuery.isFetched) return
    const storageKey = `${AUTOSTART_STORAGE_PREFIX}${location.key}`
    if (autoStartHandledKey.current === location.key) return
    if (sessionStorage.getItem(storageKey) === '1') return
    autoStartHandledKey.current = location.key
    sessionStorage.setItem(storageKey, '1')
    const next = new URLSearchParams(searchParams)
    next.delete(NON_SIGNUP_SURVEY_AUTOSTART_PARAM)
    setSearchParams(next, { replace: true })
    if (canContinue && activeResponseId) {
      handleContinueClick()
      return
    }
    if (canStart) {
      handleStartSurveyClick()
    }
  }, [
    activeResponseId,
    autoStartRequested,
    canContinue,
    canStart,
    handleContinueClick,
    handleStartSurveyClick,
    info,
    infoQuery.isFetched,
    infoQuery.isLoading,
    instrumentId,
    location.key,
    searchParams,
    setSearchParams,
    statusQuery.isFetched,
    statusQuery.isLoading,
  ])

  const phase = useMemo(
    () =>
      resolveInstrumentInfoPagePhase({
        infoLoading: infoQuery.isLoading,
        infoError: infoQuery.isError,
        infoErrorValue: infoQuery.error,
        info,
        statusFetched: statusQuery.isFetched,
        showNotEligible,
        showUnavailableScreen,
        infoLoadFailed,
        viewState,
      }),
    [
      info,
      infoLoadFailed,
      infoQuery.error,
      infoQuery.isError,
      infoQuery.isLoading,
      showNotEligible,
      showUnavailableScreen,
      statusQuery.isFetched,
      viewState,
    ],
  )

  const availabilityBadge = info
    ? deriveSurveyAvailabilityBadge({
        info,
        status,
        statusError: statusQuery.isError,
        statusLoading: statusQuery.isLoading,
      })
    : 'unavailable'

  switch (phase.kind) {
    case 'loading':
    case 'notFoundPendingStatus':
      return wrapGuestTheme(<InstrumentInfoLoadingScreen />)
    case 'notEligible':
      return wrapGuestTheme(
        <InstrumentPausedDialog
          open
          headline="Not eligible"
          message={INSTRUMENT_NOT_ELIGIBLE}
          dialogTestId="instrument-not-eligible-dialog"
        />,
      )
    case 'redirectHome':
      return <Navigate to="/" replace />
    case 'loadError':
      return wrapGuestTheme(
        <SurveyEntryScreen>
          <Box
            sx={surveyEntryCardBodySx}
            data-testid="survey-entry-load-error"
            data-survey-entry-card-body
          >
            <Alert severity="error" sx={surveyEntryStatusAlertSx}>
              Unable to load survey details. Please try again.
            </Alert>
            <Button variant="outlined" onClick={() => infoQuery.refetch()}>
              Retry
            </Button>
          </Box>
        </SurveyEntryScreen>,
      )
    case 'completed':
      if (!info) {
        return wrapGuestTheme(<InstrumentInfoLoadingScreen />)
      }
      return wrapGuestTheme(
        <SurveyCompletionView
          variant={resolveSurveyCompletionVariant({
            justSubmitted: false,
            guestClaimJourney: userGuest,
          })}
          reward={info.reward}
          instrumentRewardEnabled={info.reward.enabled}
          onClaimNow={() => navigate('/rewards')}
          onHome={goHomeOrLogin}
          webSurveySignupMode={userGuest}
          surveyFirstJourney={surveyFirstJourney}
          onCreateAccount={() => {
            persistSurveyFirstOnboardingHandoff(userId, instrumentId)
            navigate(
              surveyFirstJourney
                ? '/instrument/onboarding?surveyFirst=1'
                : '/instrument/onboarding/account',
            )
          }}
        />,
      )
    case 'unavailable':
      return wrapGuestTheme(
        <SurveyUnavailableView message={phase.message} onHome={goHomeOrLogin} />,
      )
    case 'ready':
      if (!info) {
        return wrapGuestTheme(<InstrumentInfoLoadingScreen />)
      }
      return wrapGuestTheme(
        <InstrumentInfoReadyEntry
          info={info}
          viewState={viewState}
          availabilityBadge={availabilityBadge}
          startErrorMessage={startErrorMessage}
          canStart={canStart}
          canContinue={canContinue}
          startPending={startMutation.isPending}
          statusReady={statusReady}
          statusLoading={statusQuery.isLoading}
          onStartClick={handleStartSurveyClick}
          onContinueClick={handleContinueClick}
        />,
      )
    default:
      return wrapGuestTheme(<InstrumentInfoLoadingScreen />)
  }
}
